import { Button, Tooltip, Checkbox, Modal } from "antd";
import { useSelector } from "react-redux";

const ADMIN_ROLES = ["ADMIN", "ADMIN_SUPPORT"];
export const useAuthRole = () => {
  const roles = useSelector((state) => state.auth?.roles ?? []);
  const currentPortal = useSelector((state) => state.auth?.currentPortal);
  const isAdminRole = currentPortal === "admin" && roles.some((r) => ADMIN_ROLES.includes(r));
  const isEmployee = roles.includes("EMPLOYEE") || (roles.includes("ADMIN_SUPPORT") && currentPortal==="user");
  
  return { roles, isAdminRole, isEmployee };
};

const PDFPageNavigation = ({
  currentPage, numPages, isExitingFullscreen,
  nextTimerActive, nextTimer,
  onPrev, onNext, onSubmit, showSubmitButton,
  isSubmitting,
}) => (
  <div
    className="pdf-controls-header"
    style={{ opacity: isExitingFullscreen ? 0.5 : 1, pointerEvents: isExitingFullscreen ? 'none' : 'auto' }}
  >
    <Button
      onClick={onPrev}
      disabled={currentPage === 1 || isExitingFullscreen}
      size="small"
      className="pdf-control-btn"
    >
      ⬅ Previous
    </Button>

    <div className="pdf-title">
      Page {currentPage} of {numPages}
      {numPages > 0 && (
        <span style={{ fontSize: "12px", opacity: 0.7, marginLeft: "8px" }}>
          ({Math.round((currentPage / numPages) * 100)}% complete)
        </span>
      )}
    </div>

    <Tooltip
      title={
        nextTimerActive
          ? `Please read until the timer ends (${nextTimer}s remaining).`
          : currentPage === numPages
            ? "You are on the last page."
            : "Proceed to next page."
      }
      placement="top"
    >
      <span>
        <Button
          onClick={onNext}
          disabled={nextTimerActive || currentPage === numPages || isExitingFullscreen}
          size="small"
          className="pdf-control-btn"
        >
          {nextTimerActive ? `Next ➡ (${nextTimer}s)` : "Next ➡"}
        </Button>

        {showSubmitButton && (
          <Button
            onClick={onSubmit}
            disabled={currentPage !== numPages || isExitingFullscreen || isSubmitting}
            size="small"
            className="pdf-control-btn"
            style={{ marginLeft: "10px" }}
          >
            Submit
          </Button>
        )}
      </span>
    </Tooltip>
  </div>
);

const PDFAgreementCheckbox = ({ hasAgreed, onAgree }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px 0',
    backgroundColor: '#f5f5f5',
    borderTop: '1px solid #d9d9d9',
    marginTop: '10px',
  }}>
    <Checkbox
      checked={hasAgreed}
      onChange={(e) => { if (e.target.checked) onAgree(); }}
      style={{ fontSize: '14px', fontWeight: '500' }}
    >
      I have read and agree to the above terms
    </Checkbox>
  </div>
);

const PDFCloseConfirmModal = ({ open, closeConfirmType, onConfirm, onCancel }) => (
  <Modal
    title={
      <span style={{ color: '#910E11', fontWeight: 600, paddingLeft: '20px', paddingTop: '8px' }}>
        {closeConfirmType === 'no-agreement' ? '⚠️ Confirm Close' : 'Confirm Close'}
      </span>
    }
    open={open}
    onOk={onConfirm}
    onCancel={onCancel}
    okText="Yes, Close"
    cancelText="Cancel"
    centered
    className="quiz-confirm-modal"
    zIndex={1000}
    styles={{ body: { padding: '20px' } }}
    okButtonProps={{
      danger: closeConfirmType === 'no-agreement',
      ...(closeConfirmType !== 'no-agreement' && {
        style: { backgroundColor: '#910E11', borderColor: '#910E11' },
      }),
    }}
    cancelButtonProps={{ style: { borderColor: '#910E11', color: '#910E11' } }}
  >
    {closeConfirmType === 'no-agreement' && (
      <div style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '8px', padding: '12px 16px' }}>
        <p style={{ color: '#faad14', marginBottom: '8px', fontWeight: 600 }}>⚠️ You haven't completed the course.</p>
        <p style={{ margin: 0, color: '#444' }}>Are you sure you want to close?</p>
      </div>
    )}
    {(closeConfirmType === 'general' || closeConfirmType === 'acknowledged') && (
      <div style={{ backgroundColor: '#fdf2f2', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '12px 16px' }}>
        <p style={{ margin: 0, color: '#444' }}>Are you sure you want to close this document?</p>
      </div>
    )}
  </Modal>
);

const PDFFooter = ({
  currentPage, numPages, isExitingFullscreen,
  nextTimerActive, nextTimer,
  onPrev, onNext, onSubmit,
  isAcknowledged, hasAgreed, onAgree,
  showCloseConfirm, closeConfirmType,
  onConfirmClose, onCancelClose,
  showNavigation,
  isSubmitting,
}) => {
  const { isAdminRole } = useAuthRole(); 

  return (
    <>
      {!isAdminRole && !isAcknowledged && numPages > 0 && currentPage === numPages && !nextTimerActive && (
        <PDFAgreementCheckbox hasAgreed={hasAgreed} onAgree={onAgree} />
      )}

      {showNavigation && (
        <PDFPageNavigation
          currentPage={currentPage}
          numPages={numPages}
          isExitingFullscreen={isExitingFullscreen}
          nextTimerActive={nextTimerActive}
          nextTimer={nextTimer}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={onSubmit}
          showSubmitButton={!isAdminRole}
          isSubmitting={isSubmitting}
        />
      )}

      <PDFCloseConfirmModal
        open={showCloseConfirm}
        closeConfirmType={closeConfirmType}
        onConfirm={onConfirmClose}
        onCancel={onCancelClose}
      />
    </>
  );
};

export default PDFFooter;